import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { ukDate } from "../lib/dates";
import { toast } from "sonner";
import { MessagesSquare, ArrowRight, Check, Inbox, RefreshCw } from "lucide-react";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";

export default function FeedbackBoard() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/feedback-board");
      setItems(data.items || []);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not load replies"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const markRead = async (it) => {
    setBusy(it.reply_id);
    try {
      await api.post("/feedback-board/mark-read", { submission_id: it.submission_id, reply_ids: [it.reply_id] });
      setItems((arr) => arr.filter((x) => x.reply_id !== it.reply_id));
    } catch (err) { toast.error("Could not mark as read"); }
    finally { setBusy(""); }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="feedback-board">
      <div className="sj-page-hero flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em]" style={{ color: "var(--sj-text-3)" }}><MessagesSquare className="h-4 w-4" /> Course feedback</div>
          <h1 className="mt-2 font-head text-3xl font-extrabold tracking-tight">Learner replies</h1>
          <p className="mt-1 text-sm" style={{ color: "var(--sj-text-2)" }}>Every unread reply your learners left on your feedback, across all your courses — so nothing slips through.</p>
        </div>
        <button data-testid="feedback-board-refresh" onClick={load} className="inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold hover:bg-[#E3F1EC] whitespace-nowrap" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}><RefreshCw className="h-4 w-4" /> Refresh</button>
      </div>

      {loading ? (
        <div className={`${CARD} p-10 text-center text-sm`} style={{ color: "var(--sj-text-3)" }}>Loading…</div>
      ) : items.length === 0 ? (
        <div className={`${CARD} p-12 text-center`} data-testid="feedback-board-empty">
          <Inbox className="h-10 w-10 mx-auto mb-3" style={{ color: "var(--sj-text-3)" }} />
          <div className="font-semibold" style={{ color: "var(--sj-text)" }}>You're all caught up! 🎉</div>
          <p className="mt-1 text-sm" style={{ color: "var(--sj-text-2)" }}>No unread learner replies right now.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="feedback-board-list">
          <div className="text-sm font-semibold" style={{ color: "var(--sj-text-2)" }}>{items.length} unread {items.length === 1 ? "reply" : "replies"}</div>
          {items.map((it) => (
            <div key={it.reply_id} data-testid={`feedback-item-${it.reply_id}`} className={`${CARD} p-4`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-sm font-semibold" style={{ color: "var(--sj-text)" }}>{it.learner_name}</div>
                <div className="text-xs" style={{ color: "var(--sj-text-3)" }}>{it.created_at ? ukDate(it.created_at) : ""}</div>
              </div>
              <div className="text-xs mt-0.5" style={{ color: "var(--sj-text-3)" }}>{it.course_title} · {it.lesson_title} · Page {(it.file_idx || 0) + 1}</div>
              {it.annotation_text && (
                <div className="mt-2 text-xs rounded-lg p-2" style={{ background: "#FFF7EC", color: "#8a5a1a" }}>
                  <span className="font-semibold">Your comment:</span> {it.annotation_text}
                </div>
              )}
              <div className="mt-2 flex gap-2">
                <MessagesSquare className="h-4 w-4 shrink-0 mt-0.5" style={{ color: "var(--sj-primary)" }} />
                <div className="text-sm" style={{ color: "var(--sj-text)" }}>{it.reply_text}</div>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <button data-testid={`feedback-open-${it.reply_id}`} onClick={() => navigate(`/classes/${it.course_id}`)} className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold text-white hover:brightness-105" style={{ background: "var(--sj-primary)" }}>Open course <ArrowRight className="h-4 w-4" /></button>
                <button data-testid={`feedback-read-${it.reply_id}`} disabled={busy === it.reply_id} onClick={() => markRead(it)} className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold border hover:bg-[#E3F1EC] disabled:opacity-60" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}><Check className="h-4 w-4" /> Mark read</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
